/* ==========================================================================
   Burly — global.js
   Theme-wide behavior: scroll-reveal animations (IntersectionObserver),
   sticky header, quantity inputs, dropdown/disclosure helpers, and a tiny
   pub/sub used by the cart drawer. No framework, no dependencies.
   ========================================================================== */

(function () {
  'use strict';

  /* ---- Tiny pub/sub — lets sections talk to each other (e.g. product form
     tells the cart drawer an item was added) --------------------------------- */
  window.Burly = window.Burly || {};

  Burly.events = {
    _handlers: {},
    on(event, handler) {
      (this._handlers[event] = this._handlers[event] || []).push(handler);
    },
    emit(event, payload) {
      (this._handlers[event] || []).forEach((handler) => handler(payload));
    }
  };

  /* ---- Scroll-reveal engine -------------------------------------------------
     Any element with [data-reveal] fades/slides in when scrolled into view.
     CSS handles the actual animation (base.css); we only toggle a class.
     Elements already in the first viewport reveal immediately on load so the
     page never looks broken before the first scroll. */
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initScrollReveal(root) {
    if (!document.documentElement.classList.contains('animations-enabled')) return;

    const targets = (root || document).querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (!targets.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    targets.forEach((el) => observer.observe(el));

    // Safety net: if anything is still hidden after 2.5s (a stuck observer,
    // an unusual layout the intersection check missed, etc.), force it
    // visible rather than leave real content permanently invisible.
    setTimeout(() => {
      document.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => {
        el.classList.add('is-revealed');
      });
    }, 2500);
  }

  // Re-run for content injected later (quick view, infinite scroll, editor).
  Burly.initScrollReveal = initScrollReveal;

  /* ---- Subtle parallax -------------------------------------------------------
     Elements with [data-parallax] drift as they cross the viewport.
     GPU-only (transform), driven by rAF, disabled for reduced motion.
     Optional data-parallax-speed (default 0.12; positive = slower than scroll). */
  function initParallax() {
    if (prefersReducedMotion) return;
    if (!document.documentElement.classList.contains('animations-enabled')) return;

    const els = Array.from(document.querySelectorAll('[data-parallax]'));
    if (!els.length) return;

    let ticking = false;

    function update() {
      const viewportHeight = window.innerHeight;
      els.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > viewportHeight) return;
        const speed = parseFloat(el.dataset.parallaxSpeed) || 0.12;
        const progress = (rect.top + rect.height / 2 - viewportHeight / 2) / viewportHeight;
        el.style.transform = 'translate3d(0, ' + (progress * speed * 100).toFixed(2) + 'px, 0)';
      });
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  }

  /* ---- Sticky header ---------------------------------------------------------
     Adds classes to the header section wrapper:
       .scrolled-past-top  — page is scrolled (adds shadow / solid bg)
       .header-hidden      — scrolling down (header slides away; shows on up)
     Behavior mode comes from data-sticky on the <header> element. */
  function initStickyHeader() {
    const header = document.querySelector('.site-header');
    if (!header) return;

    const mode = header.dataset.sticky || 'always';
    if (mode === 'none') return;

    let lastY = window.scrollY;

    window.addEventListener(
      'scroll',
      () => {
        const y = window.scrollY;
        header.classList.toggle('scrolled-past-top', y > 24);

        if (mode === 'on-scroll-up') {
          if (y > lastY && y > 160) {
            header.classList.add('header-hidden');
          } else {
            header.classList.remove('header-hidden');
          }
        }
        lastY = y;
      },
      { passive: true }
    );
  }

  /* ---- <quantity-input> custom element --------------------------------------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.step(button.name === 'plus' ? 1 : -1);
        });
      });
    }

    step(direction) {
      const value = parseInt(this.input.value, 10) || 0;
      const min = parseInt(this.input.min, 10) || 1;
      const next = Math.max(min, value + direction);
      if (this.input.value !== String(next)) {
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }
  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', QuantityInput);
  }

  /* ---- Close <details> dropdowns on outside click / Escape ------------------- */
  document.addEventListener('click', (event) => {
    document.querySelectorAll('details[data-close-outside][open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('details[data-close-outside][open]').forEach((details) => {
      details.removeAttribute('open');
      const summary = details.querySelector('summary');
      if (summary) summary.focus();
    });
  });

  /* ---- Boot -------------------------------------------------------------------- */
  function init() {
    initScrollReveal();
    initParallax();
    initStickyHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme Editor: re-run reveal for re-rendered sections so they don't stay hidden.
  document.addEventListener('shopify:section:load', (event) => {
    initScrollReveal(event.target);
    event.target.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
  });
})();
